/**
 * Web Audio renderer for the sound bank.
 *
 * Sounds are synthesised on the fly from the specs in `cues.ts`: no files are
 * fetched, decoded, or bundled. The graph per cue is disposable — voices are
 * created, scheduled, and left to garbage-collect once they end.
 *
 * Browsers refuse to start an AudioContext without a user gesture, so the
 * context is created lazily by `unlock()`, which the composition root calls from
 * the first key press or pointer press.
 */

import { CUES, cueDuration, type Cue, type Voice } from './cues.ts';
import type { PlayOptions, SoundBoard } from './board.ts';
import type { MusicBoard } from '../music/board.ts';
import type { ScheduledNote } from '../music/sequencer.ts';

/**
 * The music sits about 9 dB under the cue bank. The effects are the game's
 * feedback channel; the score is scenery behind them.
 */
export const MUSIC_GAIN = 0.35;

/** Counted separately from the cue bank, so a dense bar cannot starve a cue. */
export const MUSIC_MAX_VOICES = 24;

/** How long the bus takes to fade in or out when the run pauses or resumes. */
const MUSIC_FADE_MS = 200;

export interface SynthOptions {
  /** Master volume, 0..1. */
  volume?: number;
  muted?: boolean;
  /** Cap on simultaneously scheduled voices. Older cues are never cut. */
  maxVoices?: number;
}

export class WebAudioBoard implements SoundBoard {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private voices = 0;
  private readonly lastPlayed = new Map<Cue, number>();
  private readonly maxVoices: number;
  private readonly music = new MusicBus();

  private volumeValue: number;
  private mutedValue: boolean;

  constructor(options: SynthOptions = {}) {
    this.volumeValue = clamp(options.volume ?? 0.7, 0, 1);
    this.mutedValue = options.muted ?? false;
    this.maxVoices = options.maxVoices ?? 32;
  }

  get muted(): boolean {
    return this.mutedValue;
  }

  get volume(): number {
    return this.volumeValue;
  }

  /** The score's own bus. Silent until `unlock()` has built the graph. */
  get musicBus(): MusicBus {
    return this.music;
  }

  setVolume(value: number): void {
    this.volumeValue = clamp(value, 0, 1);
    this.applyGain();
  }

  setMuted(muted: boolean): void {
    this.mutedValue = muted;
    this.applyGain();
  }

  /** Returns the new state, so callers can persist it. */
  toggleMuted(): boolean {
    this.setMuted(!this.mutedValue);
    return this.mutedValue;
  }

  /**
   * Create or resume the context. Safe to call on every gesture; it is a no-op
   * once running. Never throws — a browser without Web Audio just stays silent.
   */
  unlock(): void {
    if (this.mutedValue) return;
    if (!this.ctx) this.ctx = createContext();
    if (!this.ctx) return;
    if (!this.master) {
      // A limiter keeps a tetris landing on top of a hard drop from clipping.
      const limiter = this.ctx.createDynamicsCompressor();
      limiter.threshold.value = -10;
      limiter.knee.value = 6;
      limiter.ratio.value = 12;
      limiter.attack.value = 0.003;
      limiter.release.value = 0.15;
      const master = this.ctx.createGain();
      master.connect(limiter);
      limiter.connect(this.ctx.destination);
      this.master = master;
      // The music hangs off the master gain rather than the limiter directly,
      // so the volume preset and `F3` reach it without a second code path.
      const musicGain = this.ctx.createGain();
      musicGain.connect(master);
      this.music.connect(this.ctx, musicGain, () => this.noiseBuffer());
      this.applyGain();
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
  }

  play(cue: Cue, options: PlayOptions = {}): void {
    if (this.mutedValue || !this.ctx || !this.master) return;

    const spec = CUES[cue];
    const now = this.ctx.currentTime;
    const minInterval = (spec.minIntervalMs ?? 0) / 1000;
    if (minInterval > 0) {
      const last = this.lastPlayed.get(cue);
      if (last !== undefined && now - last < minInterval) return;
    }
    this.lastPlayed.set(cue, now);

    if (this.voices >= this.maxVoices) return;

    const pitch = options.pitch ?? 1;
    const gain = (options.gain ?? 1) * (spec.gain ?? 1);
    // A hair of lead time so the first ramp is not scheduled in the past.
    const start = now + 0.005 + (options.delay ?? 0);
    for (const voice of spec.voices) this.scheduleVoice(voice, start, pitch, gain);
  }

  /** Drop every scheduled voice. Used when muting mid-run. */
  stopAll(): void {
    if (!this.ctx || !this.master) return;
    this.master.disconnect();
    this.master = null;
    this.voices = 0;
    this.lastPlayed.clear();
    // The music bus hangs off the master that just went away; `unlock` builds
    // it a new one.
    this.music.disconnect();
    this.unlock();
  }

  private applyGain(): void {
    if (!this.master || !this.ctx) return;
    const target = this.mutedValue ? 0 : this.volumeValue;
    this.master.gain.setTargetAtTime(target, this.ctx.currentTime, 0.01);
  }

  private scheduleVoice(voice: Voice, when: number, pitch: number, gainScale: number): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;

    const start = when + (voice.delay ?? 0);
    const attack = Math.max(0.001, voice.attack);
    const decay = Math.max(0.005, voice.decay);
    const end = start + attack + decay;
    const peak = Math.max(0.0002, voice.gain * gainScale);

    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, start);
    env.gain.linearRampToValueAtTime(peak, start + attack);
    // Exponential decay reads as a natural fall; it can never reach zero, so
    // the source is stopped just past the ramp instead.
    env.gain.exponentialRampToValueAtTime(0.0001, end);

    const source = this.createSource(voice, start, pitch, end);
    if (!source) return;

    let tail: AudioNode = source;
    if (voice.filter) {
      const filter = ctx.createBiquadFilter();
      filter.type = voice.filter.kind;
      filter.frequency.setValueAtTime(clamp(voice.filter.freq * pitch, 20, 20000), start);
      if (voice.filter.q !== undefined) filter.Q.setValueAtTime(voice.filter.q, start);
      tail.connect(filter);
      tail = filter;
    }
    tail.connect(env);
    env.connect(master);

    this.voices++;
    source.onended = () => {
      this.voices--;
      env.disconnect();
    };
    source.start(start);
    source.stop(end + 0.01);
  }

  private createSource(
    voice: Voice,
    start: number,
    pitch: number,
    end: number,
  ): AudioScheduledSourceNode | null {
    const ctx = this.ctx;
    if (!ctx) return null;

    if (voice.wave === 'noise') {
      const source = ctx.createBufferSource();
      source.buffer = this.noiseBuffer();
      source.loop = true;
      return source;
    }

    const osc = ctx.createOscillator();
    osc.type = voice.wave;
    const from = clamp(voice.freq * pitch, 20, 20000);
    osc.frequency.setValueAtTime(from, start);
    if (voice.toFreq !== undefined) {
      osc.frequency.exponentialRampToValueAtTime(clamp(voice.toFreq * pitch, 20, 20000), end);
    }
    if (voice.detune !== undefined) osc.detune.setValueAtTime(voice.detune, start);
    return osc;
  }

  /** One second of white noise, generated once and looped by every burst. */
  private noiseBuffer(): AudioBuffer | null {
    if (!this.ctx) return null;
    if (this.noise) return this.noise;
    const frames = this.ctx.sampleRate;
    const buffer = this.ctx.createBuffer(1, frames, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1;
    this.noise = buffer;
    return buffer;
  }
}

/**
 * Web Audio renderer for the score.
 *
 * A sibling of `WebAudioBoard` rather than part of it: the music needs its own
 * gain to duck, its own voice budget so a dense bar cannot starve a cue, and a
 * stereo stage the cue bank has no use for. It borrows the board's noise buffer
 * instead of allocating a second one.
 *
 * The graph does not exist until the board's `unlock()` has run inside a user
 * gesture, so every method is a no-op before then.
 */
export class MusicBus implements MusicBoard {
  private ctx: AudioContext | null = null;
  private gain: GainNode | null = null;
  private noise: (() => AudioBuffer | null) | null = null;
  private voices = 0;
  private playing = false;

  /** Called by `WebAudioBoard.unlock()` once the context exists. */
  connect(ctx: AudioContext, gain: GainNode, noise: () => AudioBuffer | null): void {
    this.ctx = ctx;
    this.gain = gain;
    this.noise = noise;
    this.voices = 0;
    gain.gain.setValueAtTime(this.playing ? MUSIC_GAIN : 0.0001, ctx.currentTime);
  }

  /** Forget the graph. The next `connect` builds a fresh one. */
  disconnect(): void {
    this.gain?.disconnect();
    this.gain = null;
    this.voices = 0;
  }

  note(note: ScheduledNote): void {
    const ctx = this.ctx;
    const bus = this.gain;
    // A layer scaled to nothing — the tilt drone on a level board — is skipped
    // rather than scheduled silently.
    if (!ctx || !bus || !this.playing || note.gain <= 0) return;
    if (this.voices >= MUSIC_MAX_VOICES) return;

    const start = ctx.currentTime + note.when + 0.005;
    const attack = Math.max(0.001, note.attack);
    const decay = Math.max(0.005, note.decay);
    const end = start + attack + decay;

    const source = this.createSource(note, start);
    if (!source) return;

    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, start);
    env.gain.linearRampToValueAtTime(Math.max(0.0002, note.gain), start + attack);
    env.gain.exponentialRampToValueAtTime(0.0001, end);

    let tail: AudioNode = source;
    if (note.filter) {
      const filter = ctx.createBiquadFilter();
      filter.type = note.filter.kind;
      filter.frequency.setValueAtTime(clamp(note.filter.freq, 20, 20000), start);
      if (note.filter.q !== undefined) filter.Q.setValueAtTime(note.filter.q, start);
      tail.connect(filter);
      tail = filter;
    }
    tail.connect(env);

    if (note.pan !== undefined && typeof ctx.createStereoPanner === 'function') {
      const panner = ctx.createStereoPanner();
      panner.pan.setValueAtTime(clamp(note.pan, -1, 1), start);
      env.connect(panner);
      panner.connect(bus);
    } else {
      env.connect(bus);
    }

    this.voices++;
    source.onended = () => {
      this.voices--;
      env.disconnect();
    };
    source.start(start);
    source.stop(end + 0.01);
  }

  duck(depth: number, recoveryMs: number): void {
    const ctx = this.ctx;
    const bus = this.gain;
    if (!ctx || !bus || !this.playing) return;
    const now = ctx.currentTime;
    bus.gain.cancelScheduledValues(now);
    bus.gain.setValueAtTime(bus.gain.value, now);
    bus.gain.linearRampToValueAtTime(MUSIC_GAIN * clamp(depth, 0, 1), now + 0.02);
    // `setTargetAtTime` approaches its target asymptotically; a third of the
    // recovery as the time constant lands it within a few percent on time.
    bus.gain.setTargetAtTime(MUSIC_GAIN, now + 0.02, recoveryMs / 3000);
  }

  setPlaying(playing: boolean): void {
    if (playing === this.playing) return;
    this.playing = playing;
    this.ramp(playing ? MUSIC_GAIN : 0.0001, MUSIC_FADE_MS);
  }

  stop(fadeMs: number): void {
    this.playing = false;
    this.ramp(0.0001, fadeMs);
  }

  private ramp(target: number, ms: number): void {
    const ctx = this.ctx;
    const bus = this.gain;
    if (!ctx || !bus) return;
    const now = ctx.currentTime;
    bus.gain.cancelScheduledValues(now);
    bus.gain.setValueAtTime(Math.max(0.0001, bus.gain.value), now);
    bus.gain.linearRampToValueAtTime(target, now + Math.max(0.01, ms / 1000));
  }

  private createSource(note: ScheduledNote, start: number): AudioScheduledSourceNode | null {
    const ctx = this.ctx;
    if (!ctx) return null;

    if (note.wave === 'noise') {
      const buffer = this.noise?.() ?? null;
      if (!buffer) return null;
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      return source;
    }

    const osc = ctx.createOscillator();
    osc.type = note.wave;
    osc.frequency.setValueAtTime(clamp(note.freq, 20, 20000), start);
    if (note.detune !== undefined) osc.detune.setValueAtTime(note.detune, start);
    return osc;
  }
}

/** Longest tail in the bank, for anyone sizing a fade-out. */
export function longestCueSeconds(): number {
  return (Object.keys(CUES) as Cue[]).reduce(
    (longest, cue) => Math.max(longest, cueDuration(cue)),
    0,
  );
}

function createContext(): AudioContext | null {
  const Ctor =
    typeof window === 'undefined'
      ? undefined
      : window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  try {
    return new Ctor();
  } catch {
    return null;
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
